import { Check, CircleAlert, Mic, Pencil, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { matchesApprovalCommand } from "@/features/voice-agent/approval-command";
import type {
  ApprovalChallenge,
  AssistantDocumentProposal as Proposal,
  ProposalCreationResult,
  ReadyAssistantDocumentProposal,
} from "@/features/voice-agent/assistant-document-proposals";

function label(kind: Proposal["documentKind"]): string {
  return kind === "purchase_order" ? "Purchase order" : kind[0].toUpperCase() + kind.slice(1);
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function number(value: unknown): string | null {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : text(value);
}

function previewValue(preview: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = text(preview[key]) ?? number(preview[key]);
    if (value) return value;
  }
  return null;
}

/** The purchase-order preview reports `inventoryEffects` as a record, not the plain text `previewValue` reads. */
function inventoryEffectsSummary(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  if (Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const totalQuantity = number(record.totalQuantity);
  const recordsCreated = number(record.recordsCreated);
  if (!totalQuantity || !recordsCreated) return null;
  const plural = recordsCreated === "1" ? "" : "s";
  return `${totalQuantity} units across ${recordsCreated} product record${plural} will be added to inventory.`;
}

interface ApproveContext {
  proposal: Proposal;
  challenge: ApprovalChallenge | undefined;
  command: string;
  onApprove: (
    proposal: ReadyAssistantDocumentProposal,
    challenge: string,
  ) => Promise<ProposalCreationResult>;
  submitting: { current: boolean };
  setPending: (value: boolean) => void;
  setError: (value: string | undefined) => void;
  setCreated: (value: ProposalCreationResult | undefined) => void;
}

/** Isolated from the component so its guard clauses are their own reviewable unit. */
async function approveProposal(context: ApproveContext): Promise<void> {
  const { proposal, challenge, command, onApprove, submitting, setPending, setError, setCreated } =
    context;
  if (proposal.status !== "ready" || !challenge) return;
  if (!matchesApprovalCommand(command, challenge.approvalCommand) || submitting.current) return;
  submitting.current = true;
  setPending(true);
  setError(undefined);
  try {
    setCreated(await onApprove(proposal, challenge.challenge));
  } catch {
    setError("Creation could not be confirmed. Your proposal is still available to retry.");
  } finally {
    submitting.current = false;
    setPending(false);
  }
}

interface ApproveByVoiceContext {
  proposal: Proposal;
  challenge: ApprovalChallenge | undefined;
  onVoiceApprove?: (
    proposal: ReadyAssistantDocumentProposal,
    challenge: ApprovalChallenge,
    onSubmitting: () => void,
  ) => Promise<ProposalCreationResult>;
  submitting: { current: boolean };
  canceling: { current: boolean };
  setVoiceCapturing: (value: boolean) => void;
  setPending: (value: boolean) => void;
  setError: (value: string | undefined) => void;
  setCreated: (value: ProposalCreationResult | undefined) => void;
  setCreating: (value: boolean) => void;
}

/** Isolated from the component so its guard clauses are their own reviewable unit. */
async function approveProposalByVoice(context: ApproveByVoiceContext): Promise<void> {
  const {
    proposal,
    challenge,
    onVoiceApprove,
    submitting,
    canceling,
    setVoiceCapturing,
    setPending,
    setError,
    setCreated,
    setCreating,
  } = context;
  if (proposal.status !== "ready" || !challenge) return;
  if (!onVoiceApprove || submitting.current) return;
  submitting.current = true;
  setVoiceCapturing(true);
  setPending(true);
  setError(undefined);
  try {
    const result = await onVoiceApprove(proposal, challenge, () => {
      setVoiceCapturing(false);
      setCreating(true);
    });
    if (!canceling.current) setCreated(result);
  } catch {
    if (!canceling.current)
      setError(
        "Voice confirmation was not exact or could not be verified. Type the command to retry.",
      );
  } finally {
    submitting.current = false;
    if (!canceling.current) setPending(false);
    setVoiceCapturing(false);
    setCreating(false);
  }
}

interface CancelProposalContext {
  proposal: Proposal;
  onCancel: (proposal: ReadyAssistantDocumentProposal) => Promise<unknown>;
  onCancelVoiceApproval?: () => void;
  submitting: { current: boolean };
  canceling: { current: boolean };
  setCancellationPending: (value: boolean) => void;
  setVoiceCapturing: (value: boolean) => void;
  setPending: (value: boolean) => void;
  setError: (value: string | undefined) => void;
}

/** Isolated from the component so its guard clauses are their own reviewable unit. */
async function cancelReadyProposal(context: CancelProposalContext): Promise<void> {
  const {
    proposal,
    onCancel,
    onCancelVoiceApproval,
    submitting,
    canceling,
    setCancellationPending,
    setVoiceCapturing,
    setPending,
    setError,
  } = context;
  if (proposal.status !== "ready") return;
  canceling.current = true;
  setCancellationPending(true);
  onCancelVoiceApproval?.();
  setVoiceCapturing(false);
  setPending(true);
  setError(undefined);
  let canceled = false;
  try {
    await onCancel(proposal);
    canceled = true;
  } catch {
    setError("Cancellation could not be confirmed.");
  } finally {
    if (!canceled) canceling.current = false;
    submitting.current = false;
    setPending(false);
    setCancellationPending(false);
  }
}

function ProposalReadback({ proposal }: { proposal: Proposal }) {
  const preview = proposal.preview;
  const fields = proposal.editableFields;
  const party =
    previewValue(preview, "partyName", "accountName", "customerName", "vendorName") ??
    previewValue(fields, "partyName", "accountName", "customerName", "vendorName");
  const date = previewValue(preview, "date", "issueDate", "dueDate");
  const total = previewValue(preview, "total", "grandTotal", "totalAmount");
  const lines = Array.isArray(preview.lines)
    ? preview.lines
    : Array.isArray(fields.lines)
      ? fields.lines
      : [];
  const inventory =
    previewValue(preview, "inventoryImpact") ?? inventoryEffectsSummary(preview.inventoryEffects);
  return (
    <div className="grid gap-3 text-sm">
      <dl className="grid gap-x-5 gap-y-1 sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Party</dt>
          <dd>{party ?? "Needs review"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Date</dt>
          <dd>{date ?? "Needs review"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Canonical total</dt>
          <dd>{total ?? "Needs review"}</dd>
        </div>
      </dl>
      <div>
        <p className="text-muted-foreground text-xs">Lines</p>
        <ul className="mt-1 divide-y rounded-md border" aria-label="Proposal lines">
          {lines.length ? (
            lines.map((line, index) => {
              const item =
                typeof line === "object" && line !== null ? (line as Record<string, unknown>) : {};
              const description =
                previewValue(item, "description", "name", "productName") ?? `Line ${index + 1}`;
              const quantity = previewValue(item, "quantity", "qty");
              const amount = previewValue(item, "lineTotal", "total", "amount", "unitPrice");
              return (
                <li
                  className="flex justify-between gap-3 px-3 py-2"
                  key={`${description}-${index}`}
                >
                  <span>
                    {description}
                    {quantity ? ` × ${quantity}` : ""}
                  </span>
                  <span className="text-muted-foreground">{amount ?? ""}</span>
                </li>
              );
            })
          ) : (
            <li className="px-3 py-2 text-muted-foreground">
              No lines returned for this proposal.
            </li>
          )}
        </ul>
      </div>
      {proposal.documentKind === "purchase_order" && (
        <p className="rounded-md bg-muted px-3 py-2 text-xs">
          Inventory impact: {inventory ?? "Review the canonical inventory effects before approval."}
        </p>
      )}
    </div>
  );
}

export function AssistantDocumentProposal({
  onApprove,
  onCancel,
  onChallenge,
  onDraftCustomerMessage,
  onRevise,
  onVoiceApprove,
  proposal,
  onStopVoiceApproval,
  onCancelVoiceApproval,
  voiceApprovalAvailable = false,
}: {
  onApprove: (
    proposal: ReadyAssistantDocumentProposal,
    challenge: string,
  ) => Promise<ProposalCreationResult>;
  onCancel: (proposal: ReadyAssistantDocumentProposal) => Promise<unknown>;
  onChallenge: (proposal: ReadyAssistantDocumentProposal) => Promise<ApprovalChallenge>;
  onDraftCustomerMessage?: (proposal: Proposal) => Promise<string>;
  onRevise: (proposal: Proposal) => void;
  onVoiceApprove?: (
    proposal: ReadyAssistantDocumentProposal,
    challenge: ApprovalChallenge,
    onSubmitting: () => void,
  ) => Promise<ProposalCreationResult>;
  proposal: Proposal;
  onStopVoiceApproval?: () => void;
  onCancelVoiceApproval?: () => void;
  voiceApprovalAvailable?: boolean;
}) {
  const [challenge, setChallenge] = useState<ApprovalChallenge>();
  const [customerMessage, setCustomerMessage] = useState<string>();
  const [copyError, setCopyError] = useState<string>();
  const [command, setCommand] = useState("");
  const [created, setCreated] = useState<ProposalCreationResult>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const [voiceCapturing, setVoiceCapturing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [cancellationPending, setCancellationPending] = useState(false);
  const submitting = useRef(false);
  const canceling = useRef(false);
  const receipt = proposal.status === "completed" ? proposal.result : created;
  const approve = () =>
    approveProposal({
      proposal,
      challenge,
      command,
      onApprove,
      submitting,
      setPending,
      setError,
      setCreated,
    });
  const approveByVoice = () =>
    approveProposalByVoice({
      proposal,
      challenge,
      onVoiceApprove,
      submitting,
      canceling,
      setVoiceCapturing,
      setPending,
      setError,
      setCreated,
      setCreating,
    });
  const cancelProposal = () =>
    cancelReadyProposal({
      proposal,
      onCancel,
      onCancelVoiceApproval,
      submitting,
      canceling,
      setCancellationPending,
      setVoiceCapturing,
      setPending,
      setError,
    });
  const customerCopyAvailable = proposal.documentKind !== "purchase_order";
  return (
    <section
      aria-label={`${label(proposal.documentKind)} proposal revision ${proposal.revision}`}
      className="rounded-xl border bg-muted/20 p-4"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">{label(proposal.documentKind)} proposal</h2>
          <p className="text-muted-foreground text-xs">
            Revision {proposal.revision} · {receipt ? "Created · Not sent" : "Not created or sent"}
          </p>
        </div>
        {receipt ? (
          <span className="inline-flex items-center gap-1 text-sm text-success-text">
            <Check className="size-4" />
            Created
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">Expires {proposal.expiresAt}</span>
        )}
      </div>
      <ProposalReadback proposal={proposal} />
      {customerCopyAvailable && onDraftCustomerMessage && (
        <div className="mt-4 grid gap-2 border-t pt-3">
          <Button
            onClick={() => {
              onDraftCustomerMessage(proposal)
                .then(setCustomerMessage)
                .catch(() => {
                  setError("Customer message could not be prepared.");
                });
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            Draft customer message
          </Button>
          {customerMessage && (
            <>
              <p className="text-muted-foreground text-xs">Draft only. not sent</p>
              <p className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
                {customerMessage}
              </p>
              <Button
                onClick={() => {
                  if (!navigator.clipboard) {
                    setCopyError("Copy is unavailable. Select the message text instead.");
                    return;
                  }
                  navigator.clipboard
                    .writeText(customerMessage)
                    .then(() => setCopyError(undefined))
                    .catch(() =>
                      setCopyError("Copy is unavailable. Select the message text instead."),
                    );
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                Copy message
              </Button>
              {copyError && <p className="text-destructive-text text-xs">{copyError}</p>}
            </>
          )}
        </div>
      )}
      {receipt ? (
        <div className="mt-4">
          <p className="text-sm">
            {receipt.documentNumber
              ? `${receipt.documentNumber} was created.`
              : "The document was created."}
          </p>
          {receipt.path && (
            <Link className="text-sm underline" to={receipt.path}>
              Open created {label(proposal.documentKind).toLowerCase()}
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-4 grid gap-3 border-t pt-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => onRevise(proposal)} size="sm" type="button" variant="outline">
              <Pencil className="size-3.5" />
              Revise in conversation
            </Button>
            <Button
              disabled={creating || cancellationPending || (pending && !voiceCapturing)}
              onClick={() => void cancelProposal()}
              size="sm"
              type="button"
              variant="ghost"
            >
              <X className="size-3.5" />
              Cancel proposal
            </Button>
          </div>
          {!challenge ? (
            <Button
              disabled={pending}
              onClick={() => {
                if (proposal.status !== "ready") return;
                setPending(true);
                setError(undefined);
                onChallenge(proposal)
                  .then(setChallenge)
                  .catch(() => setError("Could not start approval. Please try again."))
                  .finally(() => setPending(false));
              }}
              size="sm"
              type="button"
            >
              Review approval command
            </Button>
          ) : (
            <div className="grid gap-2 rounded-md bg-background p-3">
              <p className="text-sm">
                Type or speak this exact command to create revision {proposal.revision}:
              </p>
              <code className="select-all rounded bg-muted px-2 py-1 text-xs">
                {challenge.approvalCommand}
              </code>
              <Input
                aria-label="Approval command"
                autoComplete="off"
                onChange={(event) => setCommand(event.target.value)}
                value={command}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  disabled={
                    pending ||
                    cancellationPending ||
                    !matchesApprovalCommand(command, challenge.approvalCommand)
                  }
                  onClick={() => void approve()}
                  size="sm"
                  type="button"
                >
                  Create {label(proposal.documentKind).toLowerCase()}
                </Button>
                {voiceCapturing ? (
                  <Button onClick={onStopVoiceApproval} size="sm" type="button" variant="outline">
                    Finish spoken approval
                  </Button>
                ) : (
                  <Button
                    disabled={
                      pending || cancellationPending || !onVoiceApprove || !voiceApprovalAvailable
                    }
                    onClick={() => void approveByVoice()}
                    size="sm"
                    title={
                      voiceApprovalAvailable
                        ? "Speak the displayed command after recording starts."
                        : "Voice approval is not enabled for this assistant."
                    }
                    type="button"
                    variant="outline"
                  >
                    <Mic className="size-3.5" />
                    Speak approval
                  </Button>
                )}
              </div>
              {voiceCapturing && !creating && (
                <div className="flex items-center gap-2">
                  <p aria-live="polite" className="text-muted-foreground text-xs">
                    Recording a fresh confirmation. Finish when you have spoken the exact command.
                  </p>
                  <Button onClick={onCancelVoiceApproval} size="sm" type="button" variant="ghost">
                    Cancel spoken approval
                  </Button>
                </div>
              )}
              {creating && (
                <p aria-live="polite" className="text-muted-foreground text-xs">
                  Creating approved document…
                </p>
              )}
              {!voiceApprovalAvailable && (
                <p className="text-muted-foreground text-xs">
                  Voice approval is unavailable. Type the command instead.
                </p>
              )}
            </div>
          )}
          {error && (
            <p
              aria-live="polite"
              className="inline-flex items-center gap-1 text-destructive-text text-sm"
            >
              <CircleAlert className="size-4 text-icon-danger" />
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
